import React, { useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useAuth } from "../../utils/auth";
import { hasPermission } from "../../access/permissions";
import { canViewAnalytics } from "../../utils/roles";
import {
  fetchAnalyticsDashboard,
  fetchAnalyticsFilterOptions,
  fetchDesignImagesByCode,
} from "../../api/AnalyticsAPIs";
import {
  resolveDateRange,
  formatMoney,
  formatPercent,
  formatDateTime,
} from "../../utils/analyticsDates";
import {
  exportTopDesignsCsv,
  exportOrdersSummaryCsv,
  exportCouponPerformanceCsv,
} from "../../utils/csvExport";
import AnalyticsFilters from "./AnalyticsFilters";
import { KpiCard, SectionCard, EmptyState, LoadingBlock, ChartBox } from "./AnalyticsUi";
import MarketingOverview, { CustomerBehaviorCards } from "./MarketingOverview";
import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  BarChart,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import {
  ChevronLeft,
  Download,
  BarChart3,
  RefreshCw,
  AlertCircle,
  ImageOff,
} from "lucide-react";
import { API_BASE_URL_V2 } from "../../api/URL_Header";

const defaultDraft = () => ({
  preset: "today",
  customFrom: "",
  customTo: "",
  statuses: [],
  confirmationCategories: [],
  categoryIds: [],
  brandIds: [],
  typeIds: [],
  state: "",
  city: "",
  couponUsed: null,
  granularity: "daily",
  channel: "",
  trafficKind: "",
  campaign: "",
  medium: "",
  paymentMethod: "",
  shippingState: "",
  carrier: "",
});

const draftToApiFilters = (draft) => {
  const range = resolveDateRange(draft.preset, draft.customFrom, draft.customTo);
  return {
    startDate: range.startDate,
    endDate: range.endDate,
    statuses: draft.statuses,
    confirmationCategories: draft.confirmationCategories,
    categoryIds: draft.categoryIds,
    brandIds: draft.brandIds,
    typeIds: draft.typeIds,
    state: draft.state || undefined,
    city: draft.city || undefined,
    couponUsed: draft.couponUsed,
    granularity: draft.granularity,
    channel: draft.channel || undefined,
    trafficKind: draft.trafficKind || undefined,
    campaign: draft.campaign || undefined,
    medium: draft.medium || undefined,
    paymentMethod: draft.paymentMethod || undefined,
    shippingState: draft.shippingState || undefined,
    carrier: draft.carrier || undefined,
  };
};

const DAY_NAMES = [
  "الأحد",
  "الإثنين",
  "الثلاثاء",
  "الأربعاء",
  "الخميس",
  "الجمعة",
  "السبت",
];

function RankedList({ items, valueKey = "quantity", valueLabel = "كمية" }) {
  if (!items?.length) return <EmptyState />;
  const max = Math.max(...items.map((i) => Number(i[valueKey]) || 0), 1);
  return (
    <ul className="space-y-2.5">
      {items.map((item, idx) => {
        const val = Number(item[valueKey]) || 0;
        const pct = Math.round((val / max) * 100);
        return (
          <li key={`${item.id}-${item.name}-${idx}`} className="text-sm">
            <div className="flex justify-between gap-2 mb-1">
              <span className="font-medium text-slate-700 truncate">
                <span className="text-slate-400 text-md mr-1">#{idx + 1}</span>
                <span className="text-slate-700 text-md">{item.name}</span>
                {item.code ? (
                  <span className="text-slate-500 text-sm ml-1">({item.code})</span>
                ) : null}
              </span>
              <span className="shrink-0 text-slate-600 font-semibold">
                {valueKey === "revenue" ? formatMoney(val) : val.toLocaleString("ar-EG")}
                <span className="text-md text-slate-400 font-normal mr-1">
                  {valueKey === "revenue" ? " ج.م" : ` ${valueLabel}`}
                </span>
              </span>
            </div>
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-l from-indigo-500 to-violet-500 rounded-full"
                style={{ width: `${pct}%` }}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

/** Resolve a relative/absolute image path into a full URL */
const resolveImageSrc = (raw) => {
  if (!raw) return null;
  if (raw.startsWith("http")) return raw;
  return `${API_BASE_URL_V2}${raw.startsWith("/") ? raw : `/${raw}`}`;
};

/** Top designs: image + quantity + revenue in one ranked list */
function TopDesignsList({ items, imagesByCode = {} }) {
  if (!items?.length) return <EmptyState />;
  const maxQty = Math.max(...items.map((i) => Number(i.quantity) || 0), 1);

  return (
    <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {items.map((item, idx) => {
        const qty = Number(item.quantity) || 0;
        const revenue = Number(item.revenue) || 0;
        const pct = Math.round((qty / maxQty) * 100);
        // Image comes from the design-images endpoint, keyed by design code
        const raw =
          (item.code && imagesByCode[item.code]) ||
          item.imageUrl_1 ||
          item.imageUrl ||
          "";
        const src = resolveImageSrc(raw);

        return (
          <li
            key={`${item.id}-${idx}`}
            className="flex gap-3 rounded-xl border border-slate-100 bg-slate-50/80 p-3 hover:bg-white hover:shadow-sm transition"
          >
            <div className="relative shrink-0">
              <span className="absolute -top-1.5 -right-1.5 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-indigo-600 text-[11px] font-bold text-white shadow">
                {idx + 1}
              </span>
              {src ? (
                <img
                  src={src}
                  alt={item.name || ""}
                  className="h-20 w-20 rounded-xl object-cover border border-slate-200 bg-white"
                  loading="lazy"
                />
              ) : (
                <div className="h-20 w-20 rounded-xl border border-dashed border-slate-200 bg-slate-100 flex items-center justify-center text-slate-400">
                  <ImageOff size={22} />
                </div>
              )}
            </div>

            <div className="min-w-0 flex-1 flex flex-col justify-center gap-1.5">
              <div>
                <p className="font-semibold text-slate-800 truncate text-sm md:text-base">
                  {item.name}
                </p>
                {item.code ? (
                  <p className="text-xs text-slate-500 truncate">{item.code}</p>
                ) : null}
              </div>

              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                <span className="font-bold text-indigo-700">
                  {qty.toLocaleString("ar-EG")}
                  <span className="text-xs font-normal text-slate-500 mr-1">قطعة</span>
                </span>
                <span className="text-slate-300">|</span>
                <span className="font-bold text-emerald-700">
                  {formatMoney(revenue)}
                  <span className="text-xs font-normal text-slate-500 mr-1">ج.م</span>
                </span>
                {item.orderCount != null && (
                  <>
                    <span className="text-slate-300">|</span>
                    <span className="text-xs text-slate-500">
                      {Number(item.orderCount).toLocaleString("ar-EG")} طلب
                    </span>
                  </>
                )}
              </div>

              <div className="h-1.5 bg-slate-200/80 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-l from-indigo-500 to-violet-500 rounded-full"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function CouponTable({ data }) {
  const columns = useMemo(
    () => [
      { accessorKey: "code", header: "الكود" },
      { accessorKey: "redemptions", header: "مرات الاستخدام" },
      {
        accessorKey: "totalDiscount",
        header: "إجمالي الخصم",
        cell: (info) => formatMoney(info.getValue()),
      },
      {
        accessorKey: "totalRevenue",
        header: "الإيرادات",
        cell: (info) => formatMoney(info.getValue()),
      },
      {
        accessorKey: "averageOrderValue",
        header: "متوسط الطلب",
        cell: (info) => formatMoney(info.getValue()),
      },
    ],
    []
  );

  const table = useReactTable({
    data: data || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (!data?.length) return <EmptyState message="لا توجد كوبونات مستخدمة" />;

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm text-right">
        <thead>
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id} className="border-b border-slate-200 text-slate-500">
              {hg.headers.map((h) => (
                <th key={h.id} className="px-3 py-2 font-semibold whitespace-nowrap">
                  {flexRender(h.column.columnDef.header, h.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id} className="border-b border-slate-100 hover:bg-slate-50">
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id} className="px-3 py-2.5 text-slate-700 whitespace-nowrap">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PeakHeatmap({ cells }) {
  const grid = useMemo(() => {
    const map = new Map();
    let max = 1;
    (cells || []).forEach((c) => {
      map.set(`${c.dayOfWeek}-${c.hour}`, c.orderCount);
      if (c.orderCount > max) max = c.orderCount;
    });
    return { map, max };
  }, [cells]);

  if (!cells?.length) return <EmptyState />;

  const intensity = (count) => {
    if (!count) return "bg-slate-50";
    const t = count / grid.max;
    if (t > 0.75) return "bg-indigo-700 text-white";
    if (t > 0.5) return "bg-indigo-500 text-white";
    if (t > 0.25) return "bg-indigo-300 text-slate-800";
    return "bg-indigo-100 text-slate-700";
  };

  return (
    <div className="overflow-x-auto">
      <div className="inline-block min-w-full">
        <div className="grid gap-0.5" style={{ gridTemplateColumns: "64px repeat(24, minmax(18px, 1fr))" }}>
          <div />
          {Array.from({ length: 24 }, (_, h) => (
            <div key={h} className="text-[9px] text-center text-slate-400">
              {h}
            </div>
          ))}
          {DAY_NAMES.map((name, dow) => (
            <React.Fragment key={dow}>
              <div className="text-[10px] text-slate-500 flex items-center">{name}</div>
              {Array.from({ length: 24 }, (_, hour) => {
                const count = grid.map.get(`${dow}-${hour}`) || 0;
                return (
                  <div
                    key={hour}
                    title={`${name} ${hour}:00 — ${count} طلب`}
                    className={`h-5 rounded-sm text-[8px] flex items-center justify-center ${intensity(count)}`}
                  >
                    {count > 0 && count}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
        <p className="text-xs text-slate-400 mt-2">ساعات مصر المحلية — كلما كان اللون أغمق زاد عدد الطلبات</p>
      </div>
    </div>
  );
}

function formatPieces(value) {
  return Number(value || 0).toLocaleString("ar-EG", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function OrdersTrendTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload || {};
  const lines = [
    ["الطلبات", row.orderCount],
    ["دخلوا الموقع", row.visitors],
    ["Add to cart", row.cartAdds],
    ["Checkout", row.checkoutSessions],
    ["الإيرادات", `${formatMoney(row.revenue)} ج.م`],
  ];
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs shadow-md" dir="rtl">
      <p className="mb-1 font-semibold text-slate-800">{label}</p>
      {lines.map(([name, value]) => (
        <p key={name} className="flex items-center justify-between gap-4 py-0.5 text-slate-600">
          <span>{name}</span>
          <span className="font-semibold text-slate-900">
            {typeof value === "number" ? Number(value || 0).toLocaleString("ar-EG") : value}
          </span>
        </p>
      ))}
    </div>
  );
}

export default function AnalyticsDashboardPage() {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const [draft, setDraft] = useState(defaultDraft);
  const [applied, setApplied] = useState(() => draftToApiFilters(defaultDraft()));
  const [analyticsTab, setAnalyticsTab] = useState("all");

  React.useEffect(() => {
    if (!authState.token) {
      navigate("/login");
      return;
    }
    if (!canViewAnalytics(authState.role) && !hasPermission(authState.role, authState.permissions, "analytics.view")) {
      navigate("/dashboard");
    }
  }, [authState, navigate]);

  const optionsQuery = useQuery({
    queryKey: ["analytics-filter-options"],
    queryFn: fetchAnalyticsFilterOptions,
    staleTime: 10 * 60 * 1000,
  });

  const dashboardQuery = useQuery({
    queryKey: ["analytics-dashboard", applied],
    queryFn: () => fetchAnalyticsDashboard(applied),
    staleTime: 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const data = dashboardQuery.data;
  const kpis = data?.kpis;
  const averagePieces = (kpis?.totalOrders ?? 0) > 0 ? kpis.totalPieces / kpis.totalOrders : 0;
  const isLoading = dashboardQuery.isLoading;
  const isFetching = dashboardQuery.isFetching;
  const error = dashboardQuery.error;

  // Fetch product thumbnails by design code for the "Top designs" section
  const topDesignCodes = useMemo(
    () =>
      (data?.topPerformers?.topDesignsByQuantity || [])
        .map((d) => d.code)
        .filter(Boolean),
    [data]
  );

  const designImagesQuery = useQuery({
    queryKey: ["analytics-design-images", topDesignCodes],
    queryFn: () => fetchDesignImagesByCode(topDesignCodes),
    enabled: topDesignCodes.length > 0,
    staleTime: 10 * 60 * 1000,
  });

  const imagesByCode = designImagesQuery.data || {};

  const onApply = useCallback(() => {
    setApplied(draftToApiFilters(draft));
  }, [draft]);

  const onClear = useCallback(() => {
    const cleared = defaultDraft();
    setDraft(cleared);
    setApplied(draftToApiFilters(cleared));
  }, []);

  const setGranularity = (g) => {
    setDraft((prev) => ({ ...prev, granularity: g }));
    setApplied((prev) => ({ ...prev, granularity: g }));
  };

  const trendData = data?.trend || [];
  const waTrend = data?.whatsApp?.confirmationRateOverTime || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/40 to-slate-100" dir="rtl">
      {/* Header */}
      <header className="sticky top-0 z-30 backdrop-blur bg-white/80 border-b border-slate-200">
        <div className="max-w-[1600px] mx-auto px-3 md:px-6 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              className="inline-flex shrink-0 items-center gap-1 text-slate-600 hover:text-indigo-600 text-sm font-medium"
            >
              <ChevronLeft size={18} />
              <span>الرئيسية</span>
            </button>
            <div className="h-6 w-px bg-slate-200 shrink-0" />
            <div className="flex items-center gap-2 min-w-0">
              <div className="bg-indigo-600 text-white p-1.5 md:p-2 rounded-xl shrink-0">
                <BarChart3 size={18} />
              </div>
              <div className="min-w-0">
                <h1 className="text-base md:text-xl font-bold text-slate-900 truncate">لوحة التحليلات</h1>
                <p className="text-[11px] md:text-xs text-slate-500 truncate">
                  آخر تحديث: {formatDateTime(data?.generatedAtUtc)}
                  {isFetching && !isLoading ? " · جاري التحديث…" : ""}
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 overflow-x-auto pb-0.5 -mx-0.5">
            <button
              type="button"
              onClick={() => dashboardQuery.refetch()}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-2.5 md:px-3 py-2 text-xs md:text-sm text-slate-700 hover:bg-slate-50"
            >
              <RefreshCw size={14} className={isFetching ? "animate-spin" : ""} />
              تحديث
            </button>
            <button
              type="button"
              disabled={!data}
              onClick={() =>
                exportOrdersSummaryCsv(data?.kpis, data?.dateRange)
              }
              className="inline-flex shrink-0 items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-2.5 md:px-3 py-2 text-xs md:text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              <Download size={14} />
              ملخص CSV
            </button>
            <button
              type="button"
              disabled={!data}
              onClick={() =>
                exportTopDesignsCsv(data?.topPerformers?.topDesignsByQuantity)
              }
              className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-indigo-600 text-white px-2.5 md:px-3 py-2 text-xs md:text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
            >
              <Download size={14} />
              تصدير التصاميم
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-3 md:px-6 py-4 md:py-6">
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-2 md:gap-6 items-start">
          <AnalyticsFilters
            draft={draft}
            setDraft={setDraft}
            options={optionsQuery.data}
            onApply={onApply}
            onClear={onClear}
            isFetching={isFetching}
          />

          <div className="space-y-4 md:space-y-6 min-w-0">
            <div className="flex gap-2 overflow-x-auto pb-1">
              {[
                ["all", "الكل"],
                ["overview", "نظرة عامة"],
                ["production", "الإنتاج"],
                ["finance", "المالية"],
                ["service", "خدمة العملاء"],
                ["marketing", "التسويق"],
                ["inventory", "المخزون"],
                ["returns", "المرتجعات والجودة"],
              ].filter(([id]) => id === "all" || hasPermission(authState.role, authState.permissions, id === "service" ? "analytics.customer-service.view" : `analytics.${id}.view`)).map(([id, label]) => (
                <button key={id} type="button" onClick={() => setAnalyticsTab(id)} className={`shrink-0 rounded-xl px-3 py-2 text-xs font-bold md:text-sm ${analyticsTab === id ? "bg-slate-900 text-white" : "bg-white text-slate-600 border border-slate-200"}`}>{label}</button>
              ))}
            </div>
            {analyticsTab !== "all" && (
              <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
                {analyticsTab === "production" && <p>قطع مطبوعة: {(data?.printedPieces ?? 0).toLocaleString("ar-EG")} · دفعات: {(data?.printedBatches ?? 0).toLocaleString("ar-EG")}. تحليل الإنتاج المفصل يبقى في صفحته لمن لديه صلاحيتها.</p>}
                {analyticsTab === "finance" && <p>المبيعات مع الشحن {formatMoney(data?.kpis?.totalRevenue)} ج.م. المبيعات بدون الشحن {formatMoney(data?.kpis?.totalProfit)} ج.م. هذا ليس ربحاً صافياً.</p>}
                {analyticsTab === "service" && <p>نسبة التأكيد {formatPercent(data?.kpis?.confirmationRate)}. القوالب المرسلة {(data?.kpis?.templatesSent ?? 0).toLocaleString("ar-EG")}.</p>}
                {analyticsTab === "marketing" && <p>أداء القنوات والكوبونات والتصاميم ظاهر في تبويب الكل لنفس الفترة. هذا التبويب لا يفتح بيانات عميل كاملة.</p>}
                {analyticsTab === "inventory" && <p>لا يوجد مصدر مخزون مستقل داخل لوحة التحليلات الحالية. أرقام التوفر تبقى في شاشات الأنواع والمخزون.</p>}
                {analyticsTab === "returns" && <p>المرتجعات والاستبدال لهما صفحاتهما. هذه اللوحة لا تحسب نتائج فحص جودة غير مسجلة.</p>}
                {analyticsTab === "overview" && <p>الطلبات {(data?.kpis?.totalOrders ?? 0).toLocaleString("ar-EG")} · القطع {(data?.kpis?.totalPieces ?? 0).toLocaleString("ar-EG")}.</p>}
              </div>
            )}
            <div className={analyticsTab === "all" ? "space-y-4 md:space-y-6" : "hidden"}>
            {error && (
              <div className="flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 text-rose-800 p-4">
                <AlertCircle className="shrink-0 mt-0.5" size={20} />
                <div>
                  <p className="font-semibold">تعذر تحميل التحليلات</p>
                  <p className="text-sm mt-1">{error.message || "خطأ غير معروف"}</p>
                  <button
                    type="button"
                    onClick={() => dashboardQuery.refetch()}
                    className="mt-2 text-sm underline"
                  >
                    إعادة المحاولة
                  </button>
                </div>
              </div>
            )}

            <CustomerBehaviorCards filters={applied} />

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 md:gap-4">
              {isLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="h-28 bg-white rounded-2xl animate-pulse border border-slate-100" />
                ))
              ) : (
                <>
                  <KpiCard
                    title="إجمالي الطلبات"
                    value={(kpis?.totalOrders ?? 0).toLocaleString("ar-EG")}
                    subtitle={`${(kpis?.totalPieces ?? 0).toLocaleString("ar-EG")} قطعة`}
                    tip="عدد الطلبات، وتحتيه مجموع قطع البنود داخل نفس الطلبات"
                    accent="indigo"
                  />
                  <KpiCard
                    title="المبيعات"
                    value={`مع الشحن : ${formatMoney(kpis?.totalRevenue)} ج.م`}
                    subtitle={`بدون الشحن : ${formatMoney(kpis?.totalProfit)} ج.م`}
                    tip="الرقم الكبير مجموع الطلبات شامل الشحن. اللي تحته المبيعات من غير الشحن."
                    accent="emerald"
                  />
                  <KpiCard
                    title="متوسط قيمة الطلب"
                    value={`${formatMoney(kpis?.averageOrderValue)} ج.م`}
                    subtitle={`${formatPieces(averagePieces)} قطعة للطلب`}
                    tip="المبيعات مع الشحن ÷ عدد الطلبات. متوسط القطع = إجمالي القطع ÷ عدد الطلبات."
                    accent="sky"
                  />
                  <KpiCard
                    title="نسبة التأكيد"
                    value={formatPercent(kpis?.confirmationRate)}
                    subtitle={`${(kpis?.whatsAppConfirmed ?? 0).toLocaleString("ar-EG")} أوردر · ${formatMoney(kpis?.confirmedRevenue)} ج.م`}
                    tip="النسبة = تأكيدات واتساب ÷ القوالب المرسلة. الرقم والمبلغ للأوردرات اللي اتأكدت فعلاً."
                    accent="violet"
                  />
                  <KpiCard
                    title="تسجيلات الديوزت"
                    value={(data?.depositRegistrations?.count ?? 0).toLocaleString("ar-EG")}
                    subtitle={`${formatMoney(data?.depositRegistrations?.totalAmount)} ج.م`}
                    tip="عدد تسجيلات زرار دفع كام والمبلغ المسجل. التاريخ بتاريخ التسجيل."
                    accent="amber"
                  />
                  <KpiCard
                    title="قطع اتطبعت"
                    value={(data?.printedPieces ?? 0).toLocaleString("ar-EG")}
                    subtitle={`${(data?.printedBatches ?? 0).toLocaleString("ar-EG")} دفعة`}
                    tip="من سجل الإنتاج، بتاريخ انتهاء الطباعة في نفس الفترة. مش بتتأثر بفلاتر حالة الطلب أو الشحن."
                    accent="cyan"
                  />
                </>
              )}
            </div>

            {/* Trend */}
            <SectionCard
              title="اتجاه الإيرادات والطلبات"
              action={
                <div className="flex rounded-xl border border-slate-200 overflow-hidden text-xs font-semibold shrink-0">
                  {[
                    { id: "daily", label: "يومي" },
                    { id: "weekly", label: "أسبوعي" },
                    { id: "monthly", label: "شهري" },
                  ].map((g) => (
                    <button
                      key={g.id}
                      type="button"
                      onClick={() => setGranularity(g.id)}
                      className={`px-3 py-1.5 ${
                        draft.granularity === g.id
                          ? "bg-indigo-600 text-white"
                          : "bg-white text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      {g.label}
                    </button>
                  ))}
                </div>
              }
            >
              {isLoading ? (
                <LoadingBlock />
              ) : !trendData.length ? (
                <EmptyState />
              ) : (
                <ChartBox>
                    <ComposedChart data={trendData} margin={{ top: 8, right: 8, left: 4, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="period" tick={{ fontSize: 10 }} minTickGap={16} interval="preserveStartEnd" />
                      <YAxis
                        yAxisId="left"
                        width={44}
                        tick={{ fontSize: 10 }}
                        tickFormatter={(v) => {
                          const n = Number(v) || 0;
                          if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
                          if (Math.abs(n) >= 1000) return `${Math.round(n / 1000)}k`;
                          return String(Math.round(n));
                        }}
                      />
                      <YAxis yAxisId="right" orientation="right" width={28} tick={{ fontSize: 10 }} allowDecimals={false} />
                      <Tooltip content={<OrdersTrendTooltip />} />
                      <Legend />
                      <Bar
                        yAxisId="right"
                        dataKey="orderCount"
                        name="Orders"
                        fill="#a5b4fc"
                        radius={[4, 4, 0, 0]}
                        isAnimationActive={false}
                      />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="revenue"
                        name="Revenue"
                        stroke="#4f46e5"
                        strokeWidth={2.5}
                        dot={false}
                        isAnimationActive={false}
                      />
                    </ComposedChart>
                </ChartBox>
              )}
            </SectionCard>

            <MarketingOverview filters={applied} />

            {/* Top performers */}
            <SectionCard title="أفضل 10 تصاميم — كمية وإيرادات">
              {isLoading ? (
                <LoadingBlock />
              ) : (
                <TopDesignsList
                  items={data?.topPerformers?.topDesignsByQuantity}
                  imagesByCode={imagesByCode}
                />
              )}
            </SectionCard>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <SectionCard title="أفضل الأنواع">
                {isLoading ? <LoadingBlock /> : (
                  <RankedList items={data?.topPerformers?.topTypes} />
                )}
              </SectionCard>
              <SectionCard title="أفضل التصنيفات والبراندات">
                {isLoading ? (
                  <LoadingBlock />
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-sm font-semibold text-slate-500 mb-3">التصنيفات</h3>
                      <RankedList items={data?.topPerformers?.topCategories} />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-slate-500 mb-3">البراندات</h3>
                      <RankedList items={data?.topPerformers?.topBrands} />
                    </div>
                  </div>
                )}
              </SectionCard>
            </div>

            {/* Geographic */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <SectionCard title="الطلبات حسب المحافظة">
                {isLoading ? (
                  <LoadingBlock />
                ) : !(data?.geographic?.ordersByState || []).length ? (
                  <EmptyState />
                ) : (
                  <ChartBox className="h-72">
                      <BarChart
                        data={(data.geographic.ordersByState || []).slice(0, 15)}
                        layout="vertical"
                        margin={{ left: 8, right: 16 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis type="number" tick={{ fontSize: 11 }} />
                        <YAxis
                          type="category"
                          dataKey="name"
                          width={90}
                          tick={{ fontSize: 10 }}
                        />
                        <Tooltip />
                        <Bar dataKey="orderCount" name="Orders" fill="#6366f1" radius={[0, 4, 4, 0]} isAnimationActive={false} />
                      </BarChart>
                  </ChartBox>
                )}
              </SectionCard>
              <SectionCard title="أفضل 10 مدن">
                {isLoading ? <LoadingBlock /> : (
                  <RankedList
                    items={data?.geographic?.topCities}
                    valueKey="orderCount"
                    valueLabel="طلب"
                  />
                )}
              </SectionCard>
            </div>

            {/* WhatsApp */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <SectionCard title="أداء واتساب — نسبة التأكيد عبر الزمن">
                {isLoading ? (
                  <LoadingBlock />
                ) : !waTrend.length ? (
                  <EmptyState message="لا توجد قوالب واتساب في هذه الفترة" />
                ) : (
                  <ChartBox className="h-64">
                      <ComposedChart data={waTrend} margin={{ top: 8, right: 8, left: 4, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="period" tick={{ fontSize: 10 }} minTickGap={16} interval="preserveStartEnd" />
                        <YAxis width={32} tick={{ fontSize: 10 }} domain={[0, 100]} />
                        <Tooltip
                          formatter={(v, name) =>
                            name === "confirmationRate"
                              ? [`${v}%`, "نسبة التأكيد"]
                              : [v, name]
                          }
                        />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="confirmationRate"
                          name="confirmationRate"
                          stroke="#059669"
                          strokeWidth={2.5}
                          isAnimationActive={false}
                        />
                        <Bar dataKey="templatesSent" name="templatesSent" fill="#bbf7d0" isAnimationActive={false} />
                      </ComposedChart>
                  </ChartBox>
                )}
              </SectionCard>

              <SectionCard title="فعالية نقاط المتابعة">
                {isLoading ? (
                  <LoadingBlock />
                ) : !(data?.whatsApp?.checkpointEffectiveness || []).length ? (
                  <EmptyState />
                ) : (
                  <ul className="space-y-2">
                    {data.whatsApp.checkpointEffectiveness.map((c) => (
                      <li
                        key={c.checkpoint}
                        className="flex items-center justify-between gap-3 rounded-xl bg-slate-50 px-3 py-2.5 text-sm"
                      >
                        <span className="font-medium text-slate-700">{c.label}</span>
                        <span className="text-slate-600">
                          {c.count.toLocaleString("ar-EG")}
                          <span className="text-slate-400 text-xs mr-1">
                            ({formatPercent(c.percentage)})
                          </span>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </SectionCard>
            </div>

            <DepositPaymentSection report={data?.whatsApp?.depositPayment} loading={isLoading} />

            {/* Insights */}
            <SectionCard title="خريطة ساعات الذروة">
              {isLoading ? <LoadingBlock /> : (
                <PeakHeatmap cells={data?.insights?.peakHeatmap} />
              )}
            </SectionCard>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <SectionCard
                title="أداء الكوبونات"
                action={
                  <button
                    type="button"
                    disabled={!data?.insights?.couponPerformance?.length}
                    onClick={() =>
                      exportCouponPerformanceCsv(data?.insights?.couponPerformance)
                    }
                    className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline disabled:opacity-40"
                  >
                    <Download size={12} />
                    CSV
                  </button>
                }
              >
                {isLoading ? <LoadingBlock /> : (
                  <CouponTable data={data?.insights?.couponPerformance} />
                )}
              </SectionCard>

              <SectionCard title="العملاء المتكررون">
                {isLoading ? (
                  <LoadingBlock />
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="rounded-xl bg-slate-50 p-4">
                      <p className="text-xs text-slate-500">عملاء فريدون</p>
                      <p className="text-2xl font-bold text-slate-900 mt-1">
                        {(data?.insights?.repeatCustomers?.uniqueCustomers ?? 0).toLocaleString("ar-EG")}
                      </p>
                    </div>
                    <div className="rounded-xl bg-slate-50 p-4">
                      <p className="text-xs text-slate-500">عملاء متكررون</p>
                      <p className="text-2xl font-bold text-slate-900 mt-1">
                        {(data?.insights?.repeatCustomers?.repeatCustomers ?? 0).toLocaleString("ar-EG")}
                      </p>
                    </div>
                    <div className="rounded-xl bg-indigo-50 p-4 col-span-2">
                      <p className="text-xs text-indigo-600">نسبة التكرار (حسب رقم الهاتف)</p>
                      <p className="text-3xl font-bold text-indigo-800 mt-1">
                        {formatPercent(data?.insights?.repeatCustomers?.repeatRate)}
                      </p>
                      <p className="text-xs text-indigo-500 mt-2">
                        {(data?.insights?.repeatCustomers?.ordersFromRepeatCustomers ?? 0).toLocaleString("ar-EG")}{" "}
                        طلب من عملاء متكررين
                      </p>
                    </div>
                  </div>
                )}
              </SectionCard>
            </div>
          </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const PAYMENT_COLORS = {
  paid: "#059669",
  refused: "#e11d48",
  pending: "#f59e0b",
};

function DepositPaymentSection({ report, loading }) {
  if (loading) {
    return (
      <SectionCard title="دفع Deposit و Custom">
        <LoadingBlock />
      </SectionCard>
    );
  }
  const all = report?.all;
  if (!all || !all.total) {
    return (
      <SectionCard title="دفع Deposit و Custom">
        <EmptyState message="لا توجد طلبات Deposit أو Custom في هذه الفترة" />
      </SectionCard>
    );
  }

  const slices = [
    { key: "paid", name: "دفع", value: all.paid || 0 },
    { key: "refused", name: "مدفعش", value: all.refused || 0 },
    { key: "pending", name: "لسه مستني", value: all.pending || 0 },
  ].filter((slice) => slice.value > 0);

  return (
    <SectionCard
      title="دفع Deposit و Custom"
      subtitle="أوردرات الفترة اللي نوعها Deposit أو Custom. الدفع = Changes أو Printed أو Girabatcom أو Printing Hold أو Old. الرفض = Spam أو Canceled."
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <OutcomeStat label="عملوا أوردر" value={all.total} hint="Deposit + Custom" />
        <OutcomeStat label="دفعوا" value={all.paid} hint={formatPercent(all.paidRate)} tone="emerald" />
        <OutcomeStat label="رفضوا الدفع" value={all.refused} hint={formatPercent(all.refusedRate)} tone="rose" />
        <OutcomeStat label="لسه مستنيين" value={all.pending} hint={formatPercent(all.pendingRate)} tone="amber" />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-[280px_1fr] gap-4 items-center">
        <ChartBox className="h-56">
          <PieChart>
            <Pie data={slices} dataKey="value" nameKey="name" innerRadius={48} outerRadius={78} paddingAngle={2}>
              {slices.map((slice) => (
                <Cell key={slice.key} fill={PAYMENT_COLORS[slice.key]} />
              ))}
            </Pie>
            <Tooltip formatter={(value, name) => [Number(value).toLocaleString("ar-EG"), name]} />
          </PieChart>
        </ChartBox>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <OutcomeSplit title="Deposit" outcome={report.deposit} />
          <OutcomeSplit title="Custom" outcome={report.custom} />
        </div>
      </div>
      <p className="mt-3 text-[11px] leading-5 text-slate-500">{report.paidDefinition} {report.refusedDefinition} {report.pendingDefinition}</p>
    </SectionCard>
  );
}

function OutcomeStat({ label, value, hint, tone = "slate" }) {
  const tones = {
    slate: "bg-slate-50 text-slate-900",
    emerald: "bg-emerald-50 text-emerald-800",
    rose: "bg-rose-50 text-rose-800",
    amber: "bg-amber-50 text-amber-800",
  };
  return (
    <div className={`rounded-xl px-3 py-3 ${tones[tone] || tones.slate}`}>
      <p className="text-xs opacity-70">{label}</p>
      <p className="mt-1 text-2xl font-bold">{Number(value || 0).toLocaleString("ar-EG")}</p>
      <p className="text-xs opacity-70">{hint}</p>
    </div>
  );
}

function OutcomeSplit({ title, outcome }) {
  const row = outcome || {};
  return (
    <div className="rounded-xl border border-slate-200 p-3 text-sm">
      <p className="font-bold text-slate-800 mb-2">{title}</p>
      <p className="flex justify-between py-1"><span>أوردرات</span><span>{Number(row.total || 0).toLocaleString("ar-EG")}</span></p>
      <p className="flex justify-between py-1 text-emerald-700"><span>دفع</span><span>{Number(row.paid || 0).toLocaleString("ar-EG")} ({formatPercent(row.paidRate)})</span></p>
      <p className="flex justify-between py-1 text-rose-700"><span>مدفعش</span><span>{Number(row.refused || 0).toLocaleString("ar-EG")} ({formatPercent(row.refusedRate)})</span></p>
      <p className="flex justify-between py-1 text-amber-700"><span>لسه مستني</span><span>{Number(row.pending || 0).toLocaleString("ar-EG")} ({formatPercent(row.pendingRate)})</span></p>
    </div>
  );
}
